import { z } from "zod";

/**
 * Tool outputs as MCP results, for eval's `tools`: shared by the host (worker
 * runs, Hearth operations) and the browser (page runs), so a result has one
 * shape whichever runtime produced it.
 */
import type { McpResult } from "../mcp/contract";

export function mcpError(text: string): McpResult {
  return { isError: true, content: [{ type: "text", text }] };
}

/** Data as MCP: structured when it is an object, with its JSON as text. */
export function mcpData(value: unknown): McpResult {
  return {
    content: [{ type: "text", text: JSON.stringify(value) ?? "null" }],
    ...(value !== null && typeof value === "object" && !Array.isArray(value)
      ? { structuredContent: value as Record<string, unknown> }
      : {}),
  };
}

// The App-run contract (`HearthDO.runAppTool`, the page runtime): a failed
// run returns `{ error }`. It goes once Apps return MCP results themselves.
const appRunFailure = z.object({ error: z.string() });

// How an App tool returns media: the AI SDK's content output.
const appContent = z.object({
  type: z.literal("content"),
  value: z.array(
    z.union([
      z.object({ type: z.literal("text"), text: z.string() }),
      z.object({
        type: z.enum(["image-data", "file-data", "media"]),
        data: z.string(),
        mediaType: z.string(),
      }),
    ]),
  ),
});

type Block = McpResult["content"][number];

function block(part: z.infer<typeof appContent>["value"][number]): Block {
  if (part.type === "text") return { type: "text", text: part.text };
  const { data, mediaType } = part;
  if (mediaType.startsWith("image/"))
    return { type: "image", data, mimeType: mediaType };
  if (mediaType.startsWith("audio/"))
    return { type: "audio", data, mimeType: mediaType };
  return {
    type: "resource",
    resource: { uri: "data:", blob: data, mimeType: mediaType },
  };
}

// An output schema that declares that content shape describes media, not data.
const contentEnvelope = z.object({
  properties: z.object({
    type: z.union([
      z.object({ const: z.literal("content") }),
      z.object({ enum: z.tuple([z.literal("content")]) }),
    ]),
  }),
});

/**
 * An App tool's structured output schema: none when it declares the content
 * shape, whose result is media (`McpContent`), not `structuredContent`.
 */
export function appStructuredOutput(
  schema: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  return schema && !contentEnvelope.safeParse(schema).success
    ? schema
    : undefined;
}

/** An App tool's output, from a worker or a page run, as MCP. */
export function appRunResult(output: unknown): McpResult {
  const failed = appRunFailure.safeParse(output);
  if (failed.success) return mcpError(failed.data.error);
  const content = appContent.safeParse(output);
  return content.success
    ? { content: content.data.value.map(block) }
    : mcpData(output);
}
